import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

const OutlookOAuthCallback = () => {
  const [status, setStatus] = useState('processing')
  const navigate = useNavigate()

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        // Get the authorization code from URL
        const urlParams = new URLSearchParams(window.location.search)
        const code = urlParams.get('code')
        const state = urlParams.get('state') // prefixed state e.g. 'outlook:<tenantId>' or 'outlook-calendar:<tenantId>'
        const error = urlParams.get('error')

        if (error) {
          console.error('OAuth error:', error)
          setStatus('error')
          toast.error('Outlook authorization failed: ' + error)
          
          // Close popup and redirect parent
          if (window.opener) {
            window.opener.postMessage({ type: 'OUTLOOK_OAUTH_ERROR', error }, '*')
            window.close()
          } else {
            setTimeout(() => navigate('/dashboard'), 3000)
          }
          return
        }

        if (!code) {
          setStatus('error')
          toast.error('No authorization code received')
          
          if (window.opener) {
            window.opener.postMessage({ type: 'OUTLOOK_OAUTH_ERROR', error: 'No code' }, '*')
            window.close()
          } else {
            setTimeout(() => navigate('/dashboard'), 3000)
          }
          return
        }

        console.log('✅ Authorization code received:', code.substring(0, 20) + '...')
        console.log('🏢 Tenant ID (state):', state)

        // Exchange authorization code for access token
        const CLIENT_ID = import.meta.env.VITE_MICROSOFT_CLIENT_ID
        const CLIENT_SECRET = import.meta.env.VITE_MICROSOFT_CLIENT_SECRET

        if (!CLIENT_ID || !CLIENT_SECRET) {
          console.error('Microsoft OAuth client credentials are not configured.')
          setStatus('error')
          toast.error('Microsoft OAuth configuration is missing. Please configure VITE_MICROSOFT_CLIENT_ID and VITE_MICROSOFT_CLIENT_SECRET.')

          if (window.opener) {
            window.opener.postMessage({ type: 'OUTLOOK_OAUTH_ERROR', error: 'Missing Microsoft OAuth credentials' }, '*')
            window.close()
          } else {
            setTimeout(() => navigate('/dashboard'), 3000)
          }
          return
        }

        const REDIRECT_URI = window.location.origin + '/oauth/outlook/callback'

        const tokenResponse = await fetch('https://login.microsoftonline.com/common/oauth2/v2.0/token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            code: code,
            client_id: CLIENT_ID,
            client_secret: CLIENT_SECRET,
            redirect_uri: REDIRECT_URI,
            grant_type: 'authorization_code',
            scope: 'openid profile email offline_access Mail.Send Calendars.ReadWrite User.Read'
          }),
        })

        const tokenData = await tokenResponse.json()

        if (tokenData.error) {
          console.error('Token exchange error:', tokenData.error)
          setStatus('error')
          toast.error('Token exchange failed: ' + tokenData.error)
          
          if (window.opener) {
            window.opener.postMessage({ type: 'OUTLOOK_OAUTH_ERROR', error: tokenData.error }, '*')
            window.close()
          } else {
            setTimeout(() => navigate('/dashboard'), 3000)
          }
          return
        }

        console.log('✅ Tokens received successfully')
        
        // Get user's email address from Microsoft Graph
        let userEmail = null
        try {
          const profileResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
            headers: {
              'Authorization': `Bearer ${tokenData.access_token}`
            }
          })
          if (profileResponse.ok) {
            const profileData = await profileResponse.json()
            userEmail = profileData.mail || profileData.userPrincipalName
            console.log('✅ Outlook email from profile API:', userEmail)
          }
        } catch (profileError) {
          console.warn('Could not fetch Outlook profile:', profileError)
        }

        setStatus('success')
        toast.success('🎉 Outlook connected successfully!')

        // Determine the success message type based on state
        const isCalendar = state?.startsWith('outlook-calendar:')
        const successType = isCalendar ? 'OUTLOOK_CALENDAR_OAUTH_SUCCESS' : 'OUTLOOK_OAUTH_SUCCESS'
        const tenantId = state?.includes(':') ? state.split(':')[1] : state

        // Notify parent window and close popup
        if (window.opener) {
          window.opener.postMessage({ 
            type: successType, 
            tenantId,
            tokens: {
              access_token: tokenData.access_token,
              refresh_token: tokenData.refresh_token,
              expires_in: tokenData.expires_in,
              email: userEmail || (isCalendar ? 'Outlook Calendar Connected' : 'Outlook Connected')
            }
          }, '*')
          
          setTimeout(() => {
            window.close()
          }, 2000)
        } else {
          setTimeout(() => navigate('/dashboard'), 3000)
        }

      } catch (error) {
        console.error('OAuth callback error:', error)
        setStatus('error')
        toast.error('OAuth processing failed: ' + error.message)
        
        if (window.opener) {
          window.opener.postMessage({ type: 'OUTLOOK_OAUTH_ERROR', error: error.message }, '*')
          window.close()
        } else {
          setTimeout(() => navigate('/dashboard'), 3000)
        }
      }
    }

    handleOAuthCallback()
  }, [navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-white">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4">
        <div className="text-center">
          {status === 'processing' && (
            <>
              <div className="text-6xl mb-4">🔄</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Processing Outlook Authorization</h2>
              <p className="text-gray-600">Please wait while we connect your Outlook account...</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="text-6xl mb-4">✅</div>
              <h2 className="text-2xl font-bold text-green-600 mb-2">Success!</h2>
              <p className="text-gray-600 mb-4">Your Outlook account has been connected successfully.</p>
              <p className="text-sm text-gray-500">This window will close automatically...</p>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="text-6xl mb-4">❌</div>
              <h2 className="text-2xl font-bold text-red-600 mb-2">Connection Failed</h2>
              <p className="text-gray-600 mb-4">There was an error connecting your Outlook account.</p>
              <p className="text-sm text-gray-500">This window will close automatically...</p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default OutlookOAuthCallback
